import { Platform } from '../types/Platform';

// The player's own handle per platform, in localStorage so signed-out visitors don't retype it. Keyed
// by platform name, not enum value, so reordering Platform can't repoint a handle. Register.cshtml
// reads this key with plain JS; keep the two in sync.
export const HANDLES_KEY = 'syc.handles';

export type PlatformName = 'ChessCom' | 'Lichess';
export type Handles = Partial<Record<PlatformName, string>>;

export function platformName(platform: Platform): PlatformName | null {
  return platform === Platform.ChessCom
    ? 'ChessCom'
    : platform === Platform.Lichess
      ? 'Lichess'
      : null;
}

// Every access is wrapped: localStorage throws in Safari private mode and when storage is blocked.
export function readHandles(): Handles {
  try {
    const raw = localStorage.getItem(HANDLES_KEY);
    if (raw == null) {
      return {};
    }
    const parsed = JSON.parse(raw) as unknown;
    if (parsed == null || typeof parsed !== 'object') {
      return {};
    }
    const handles: Handles = {};
    for (const name of ['ChessCom', 'Lichess'] as PlatformName[]) {
      const value = (parsed as Record<string, unknown>)[name];
      if (typeof value === 'string' && value.trim() !== '') {
        handles[name] = value.trim();
      }
    }
    return handles;
  } catch {
    return {};
  }
}

export function writeHandles(handles: Handles): void {
  try {
    localStorage.setItem(HANDLES_KEY, JSON.stringify(handles));
  } catch {
    // Storage unavailable — the handle just won't survive the session.
  }
}

// Every handle typed into the import box, newest first. The map above keeps one per platform; this
// remembers a second account on the same site for the suggestions.
export const RECENT_USERNAMES_KEY = 'syc.recentUsernames';
const RECENT_USERNAMES_MAX = 20;

export function readRecentUsernames(): string[] {
  try {
    const raw = localStorage.getItem(RECENT_USERNAMES_KEY);
    if (raw == null) {
      return [];
    }
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed.filter((v): v is string => typeof v === 'string' && v.trim() !== '');
  } catch {
    return [];
  }
}

// Add a name to the front, de-duped case-insensitively, and persist. Returns the new list.
export function addRecentUsername(name: string): string[] {
  const trimmed = name.trim();
  if (trimmed === '') {
    return readRecentUsernames();
  }
  const rest = readRecentUsernames().filter(
    (u) => u.toLowerCase() !== trimmed.toLowerCase(),
  );
  const next = [trimmed, ...rest].slice(0, RECENT_USERNAMES_MAX);
  try {
    localStorage.setItem(RECENT_USERNAMES_KEY, JSON.stringify(next));
  } catch {
    // Storage unavailable — suggestions just won't persist this session.
  }
  return next;
}
